import { Injectable } from '@angular/core';
import { merge, Observable, of } from 'rxjs';
import { map, scan } from 'rxjs/operators';
import { DataManagerService } from '../../../../data/services/DataManager/data-manager.service';
import { NavTabContext } from '../../../nav-drawer/nav-tabs';
import { TopbarSearchResult } from '../topbar-search.model';
import { looksLikeCardNumber } from './card-number-guard';
import { SearchProvider } from './record-search.model';
import { batchesProvider } from './providers/batches.provider';
import { chargesProvider } from './providers/charges.provider';
import { customersProvider } from './providers/customers.provider';
import { depositsProvider } from './providers/deposits.provider';
import { disputesProvider } from './providers/disputes.provider';

/** Shorter queries match too much to be worth a request. */
export const MIN_RECORD_QUERY_LENGTH = 3;

/** Results so far, in provider order, and whether any provider is still running. */
export interface RecordSearchUpdate {
  results: TopbarSearchResult[];
  pending: boolean;
}

const DONE: RecordSearchUpdate = { results: [], pending: false };

/**
 * Record search for the topbar panel: a parallel fan-out over the existing list endpoints.
 * A provider runs only when its page is in the user's menu and its own rule passes — the same
 * gate as the navigation tabs — so nobody is offered records from a page they cannot open.
 */
@Injectable({ providedIn: 'root' })
export class RecordSearchService {
  /** Registration order = group order in the panel. */
  readonly providers: SearchProvider[];

  constructor(dataManager: DataManagerService) {
    this.providers = [
      chargesProvider(dataManager),
      batchesProvider(dataManager),
      // PayPal merchants' batches: the settlement day (YYYYMMDD) on Deposits (Batches is hidden for them)
      depositsProvider(dataManager),
      disputesProvider(dataManager),
      customersProvider(dataManager)
    ];
  }

  /**
   * Emits once per provider as each one answers, so a fast group (e.g. Customers) shows without
   * waiting for a slow one (e.g. Transactions via Redshift). Each emission holds every group
   * answered so far, still in provider order; the last one has `pending: false`.
   * @param menuRoots route roots of the user's menu pages (e.g. 'payment', 'customer')
   * @param ctx permissions / account settings, as for NAV_TABS
   */
  search(query: string, menuRoots: Set<string>, ctx: NavTabContext): Observable<RecordSearchUpdate> {
    const q = (query || '').trim();
    // PCI: repeated here (not only in the panel) so no caller can send a card-number-like query.
    if (q.length < MIN_RECORD_QUERY_LENGTH || looksLikeCardNumber(q)) {
      return of(DONE);
    }
    const active = this.providers.filter(p =>
      menuRoots.has(p.parent) && (!p.visible || p.visible(ctx)) && p.accepts(q));
    if (!active.length) {
      return of(DONE);
    }
    return merge(...active.map((p, i) => p.search(q).pipe(map(rows => ({ i, rows: rows || [] }))))).pipe(
      scan((byProvider, answer) => {
        const next = byProvider.slice();
        next[answer.i] = answer.rows;
        return next;
      }, new Array<TopbarSearchResult[]>(active.length)),
      map(byProvider => ({
        results: byProvider.reduce((all, g) => all.concat(g || []), [] as TopbarSearchResult[]),
        pending: byProvider.filter(Boolean).length < active.length
      }))
    );
  }
}
